import React, { useRef } from "react";
import { useTranslation } from "react-i18next";

import { IosDevice } from "./IosDevice";
import { Ico } from "./malluIcons";
import { MALLU_MARK_SRC } from "./malluMark";
import { MalluAppScreen } from "./MalluAppScreen";
import { MalluCardMock } from "./MalluCardMock";
import { scrollToMalluSection } from "./MalluNav";
import {
  HERO_RIBBONS,
  useCountUp,
  useHeroParallax,
  useRibbon,
  useTilt,
} from "./malluMotion";

const HERO_POSE = { pre: "perspective(1600px) ", ry: -14, rx: 6 };
const BALANCE_TARGET = 12480.9;

const asList = (value) => (Array.isArray(value) ? value : []);

export const MalluHero = ({ urls, motion, toasts = true }) => {
  const { t } = useTranslation("malluLanding");
  const canvasRef = useRef(null);
  const copyRef = useRef(null);
  const visualRef = useRef(null);
  const phoneRef = useRef(null);
  const balanceRef = useRef(null);

  useRibbon(canvasRef, HERO_RIBBONS, motion);
  useHeroParallax(copyRef, visualRef, motion);
  useCountUp(balanceRef, BALANCE_TARGET, motion);
  const tilt = useTilt(phoneRef, { max: 10, base: HERO_POSE, enabled: motion > 0 });

  const checks = asList(t("hero.checks", { returnObjects: true }));
  const toastItems = asList(t("hero.toasts", { returnObjects: true }));

  return (
    <section
      id="inicio"
      className="pp-hero"
      onMouseMove={tilt.onMouseMove}
      onMouseLeave={tilt.onMouseLeave}
    >
      <img src={MALLU_MARK_SRC} alt="" aria-hidden="true" className="pp-hero-watermark" />
      <canvas ref={canvasRef} className="pp-canvas" aria-hidden="true" />
      <div className="pp-hero-glow" aria-hidden="true" />
      <div className="pp-hero-beam" aria-hidden="true" />
      <div className="pp-hero-shade" aria-hidden="true" />
      <div className="pp-hero-fade" aria-hidden="true" />

      <div className="pp-hero-grid">
        <div ref={copyRef} className="pp-hero-copy">
          <h1 className="pp-hero-title">
            <span className="rv-child pp-hero-line1" data-delay="150">
              <span>{t("hero.title1")}</span>
            </span>
            <span className="rv-child pp-hero-line2" data-delay="320">
              <span>{t("hero.title2")}</span>
            </span>
          </h1>
          <p className="pp-hero-lead rv rv-y24" data-delay="500">
            {t("hero.lead")}
          </p>
          <div className="pp-hero-ctas rv rv-y24" data-delay="650">
            <a
              className="pp-btn pp-btn--primary"
              href={urls.register}
              target="_blank"
              rel="noreferrer"
            >
              {t("hero.ctaPrimary")} <Ico name="arrow" size={18} sw={2.2} />
            </a>
            <a
              className="pp-btn pp-btn--ghost"
              href="/#empresas"
              onClick={(e) => {
                e.preventDefault();
                scrollToMalluSection("empresas");
              }}
            >
              {t("hero.ctaSecondary")}
            </a>
          </div>
          <div className="pp-hero-checks rv rv-y20" data-delay="800">
            {checks.map((label) => (
              <span className="pp-check" key={label}>
                <Ico name="check" size={16} color="#ff68b5" sw={2.4} />
                {label}
              </span>
            ))}
          </div>
        </div>

        <div ref={visualRef} className="pp-hero-visual">
          <div className="pp-hero-stage rv rv-phone" data-delay="300">
            <div className="pp-hero-halo" aria-hidden="true" />
            <div className="pp-float">
              <div ref={phoneRef} className="pp-phone">
                <div className="pp-phone-scale">
                  <IosDevice>
                    <MalluAppScreen t={t} balanceRef={balanceRef} />
                  </IosDevice>
                </div>
              </div>
            </div>

            <div className="pp-hero-card-wrap" aria-hidden="true">
              <div className="rv rv-card" data-delay="750">
                <MalluCardMock t={t} />
              </div>
            </div>

            {toasts && toastItems.length ? (
              <div className="pp-toasts" aria-hidden="true">
                {toastItems.slice(0, 2).map((toast, i) => (
                  <div className={`pp-toast pp-toast--${i + 1}`} key={toast.title}>
                    <div className={`pp-toast-ico ${i === 0 ? "pp-toast-ico--cyan" : "pp-toast-ico--blue"}`}>
                      <Ico
                        name={i === 0 ? "pix" : "card"}
                        size={18}
                        color={i === 0 ? "#ff68b5" : "#ff4fa6"}
                        sw={2}
                      />
                    </div>
                    <div>
                      <div className="pp-toast-title">{toast.title}</div>
                      <div className="pp-toast-meta">{toast.meta}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="pp-scroll-hint" aria-hidden="true">
        <span className="pp-scroll-line" />
        <span className="pp-scroll-text">{t("hero.scrollHint")}</span>
      </div>
    </section>
  );
};
